import { Injectable } from '@angular/core';
import { GraphHistoryActionType } from 'src/app/core/models/graph-history.model';

/**
 * Pista ("hint") del action_type que el frontend conoce al ejecutar una
 * mutación HTTP del grafo. El `ActionHintInterceptor` lee la cima de la pila
 * y añade el header `X-Action-Hint` a la petición saliente. El backend usa
 * esa pista para escribir el `action_type` exacto en el historial (p. ej.
 * distinguir RENAME_IDEA vs UPDATE_IDEA_STYLE vs UPDATE_IDEA_DESCRIPTION
 * cuando todas pasan por el mismo endpoint de "save node").
 *
 * Uso típico:
 *
 *   this.hintService.pushHint('MOVE_IDEA');
 *   try {
 *     this.sigmaCanvasService.savePosition(...); // lleva X-Action-Hint: MOVE_IDEA
 *   } finally {
 *     this.hintService.popHint();
 *   }
 *
 * o, equivalente para código promise-based:
 *
 *   await this.hintService.withHint('UPDATE_IDEA_STYLE', () => doThings());
 */
@Injectable({ providedIn: 'root' })
export class HistoryHintService {

	private stack: GraphHistoryActionType[] = [];

	/** Hint activo (cima de la pila) o null. */
	get currentHint(): GraphHistoryActionType | null {
		return this.stack.length > 0 ? this.stack[this.stack.length - 1] : null;
	}

	pushHint(hint: GraphHistoryActionType): void {
		this.stack.push(hint);
	}

	popHint(): void {
		this.stack.pop();
	}

	/** Ejecuta fn con el hint activo, lo saca siempre al terminar (sync). */
	runWithHintSync<T>(hint: GraphHistoryActionType, fn: () => T): T {
		this.pushHint(hint);
		try { return fn(); }
		finally { this.popHint(); }
	}

	/** Ejecuta fn con el hint activo (promise). Limpia al terminar. */
	async withHint<T>(hint: GraphHistoryActionType, fn: () => Promise<T>): Promise<T> {
		this.pushHint(hint);
		try { return await fn(); }
		finally { this.popHint(); }
	}

	clear(): void {
		this.stack = [];
	}
}
